import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { CheckCircleFill, Flag, HourglassSplit, XCircleFill, XLg } from 'react-bootstrap-icons';
import { useLocale } from '../i18n/LocaleContext';
import { apiErrorCode, createLabelDispute } from '../api/brands';
import { labelingFinished } from './productLabeling';
import './LabelDispute.scss';

export const DISPUTE_MESSAGE_MAX = 1000;
export const APPROVED_VISIBLE_DAYS = 14;

function formatDate(value, lang) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat(lang === 'hu' ? 'hu-HU' : 'en-GB', {
    year: 'numeric', month: 'short', day: 'numeric',
  }).format(d);
}

function recentlyResolved(dispute) {
  const at = dispute?.resolved_at ? new Date(dispute.resolved_at).getTime() : NaN;
  return Number.isFinite(at) && Date.now() - at < APPROVED_VISIBLE_DAYS * 86400000;
}

export default function LabelDispute({ product, brandId, dispute, canDispute = false, onDisputed }) {
  const { t, lang } = useLocale();
  const [open, setOpen] = useState(false);

  if (typeof product?.is_clothing !== 'boolean') return null;

  const status = dispute?.status;
  const offer = canDispute && product.is_clothing === false && labelingFinished(product) && status !== 'pending';
  const showApproved = status === 'approved' && product.is_clothing === true && recentlyResolved(dispute);
  const showRejected = status === 'rejected' && product.is_clothing === false;

  if (status !== 'pending' && !offer && !showApproved && !showRejected) return null;

  const modal = open && (
    <LabelDisputeModal
      product={product}
      brandId={brandId}
      onClose={() => setOpen(false)}
      onDisputed={(created) => {
        setOpen(false);
        if (onDisputed) onDisputed(product.id, created);
      }}
    />
  );

  if (status === 'pending') {
    return (
      <div className="label-dispute">
        <div className="label-dispute__result label-dispute__result--pending" role="status">
          <div className="label-dispute__result-head">
            <HourglassSplit size={12} aria-hidden="true" />
            {t('products.disputePending')}
          </div>
          <div className="label-dispute__result-meta">
            {t('products.disputeSentOn', { date: formatDate(dispute.created_at, lang) })}
          </div>
          {dispute.message && <div className="label-dispute__result-quote">{dispute.message}</div>}
        </div>
      </div>
    );
  }

  return (
    <div className="label-dispute">
      {showApproved && (
        <div className="label-dispute__result label-dispute__result--approved" role="status">
          <div className="label-dispute__result-head">
            <CheckCircleFill size={12} aria-hidden="true" />
            {t('products.disputeApproved')}
          </div>
          <div className="label-dispute__result-meta">
            {dispute.resolved_category
              ? t('products.disputeApprovedAsCategory', { category: dispute.resolved_category })
              : t('products.disputeApprovedAs')}
            {' · '}
            {formatDate(dispute.resolved_at, lang)}
          </div>
          {dispute.admin_note && <div className="label-dispute__result-quote">{dispute.admin_note}</div>}
        </div>
      )}
      {showRejected && (
        <div className="label-dispute__result label-dispute__result--rejected" role="status">
          <div className="label-dispute__result-head">
            <XCircleFill size={12} aria-hidden="true" />
            {t('products.disputeRejected')}
          </div>
          <div className="label-dispute__result-meta">{formatDate(dispute.resolved_at, lang)}</div>
          <div className="label-dispute__result-quote">
            {dispute.admin_note || t('products.disputeNoReason')}
          </div>
        </div>
      )}
      {offer && (
        <button
          type="button"
          className={showRejected ? 'label-dispute__retry' : 'label-dispute__action'}
          onClick={() => setOpen(true)}
        >
          <Flag size={11} aria-hidden="true" />
          {showRejected ? t('products.disputeAgain') : t('products.disputeAction')}
        </button>
      )}
      {modal}
    </div>
  );
}

function LabelDisputeModal({ product, brandId, onClose, onDisputed }) {
  const { t } = useLocale();
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [errorKey, setErrorKey] = useState(null);
  const alive = useRef(true);
  const textareaRef = useRef(null);

  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; };
  }, []);

  useEffect(() => {
    if (textareaRef.current) textareaRef.current.focus();
    function onKey(e) {
      if (e.key === 'Escape') closeRef.current();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  async function submit(e) {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setErrorKey(null);
    try {
      const created = await createLabelDispute(brandId, product.id, message);
      if (!alive.current) return;
      onDisputed(created && created.status ? created : { product_id: product.id, status: 'pending' });
    } catch (err) {
      if (!alive.current) return;
      const code = apiErrorCode(err);
      if (err?.status === 409 && code === 'dispute_already_pending') {
        onDisputed({ product_id: product.id, status: 'pending' });
        return;
      }
      if (err?.status === 409 && code === 'product_not_disputable') {
        setErrorKey('products.disputeNotDisputable');
      } else {
        setErrorKey('products.disputeFailed');
      }
      setBusy(false);
    }
  }

  return createPortal(
    <div className="label-dispute-modal" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <form
        className="label-dispute-modal__dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="label-dispute-title"
        onSubmit={submit}
      >
        <div className="label-dispute-modal__head">
          <h3 id="label-dispute-title" className="label-dispute-modal__title">{t('products.disputeTitle')}</h3>
          <button
            type="button"
            className="label-dispute-modal__close"
            onClick={onClose}
            aria-label={t('products.disputeCancel')}
          >
            <XLg size={14} aria-hidden="true" />
          </button>
        </div>
        <div className="label-dispute-modal__product">{product.name || product.title || '—'}</div>
        <div className="label-dispute-modal__verdict">{t('products.disputeVerdictNotClothing')}</div>
        <p className="label-dispute-modal__ask">{t('products.disputeAskClothing')}</p>
        <label className="label-dispute-modal__field">
          <span>{t('products.disputeMessage')}</span>
          <textarea
            ref={textareaRef}
            rows={4}
            value={message}
            maxLength={DISPUTE_MESSAGE_MAX}
            placeholder={t('products.disputeMessageHint')}
            onChange={(e) => setMessage(e.target.value)}
            disabled={busy}
          />
          <small className="label-dispute-modal__count">{message.length}/{DISPUTE_MESSAGE_MAX}</small>
        </label>
        {errorKey && <div className="label-dispute-modal__error" role="alert">{t(errorKey)}</div>}
        <div className="label-dispute-modal__actions">
          <button type="button" className="btn-o btn-o--ghost" onClick={onClose} disabled={busy}>
            {t('products.disputeCancel')}
          </button>
          <button type="submit" className="btn-o btn-o--brand" disabled={busy}>
            {busy ? t('products.disputeSending') : t('products.disputeSubmit')}
          </button>
        </div>
      </form>
    </div>,
    document.body
  );
}
